import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SyringeVisualization } from "./SyringeVisualization";

// Dose options in mg
const DOSE_OPTIONS = [
  { value: "0.1", label: "0.1mg" },
  { value: "0.25", label: "0.25mg" },
  { value: "0.5", label: "0.5mg" },
  { value: "1", label: "1mg" },
  { value: "1.5", label: "1.5mg" },
  { value: "2", label: "2mg" },
  { value: "2.5", label: "2.5mg" },
  { value: "3", label: "3mg" },
  { value: "4", label: "4mg" },
  { value: "4.5", label: "4.5mg" },
  { value: "5", label: "5mg" },
  { value: "6", label: "6mg" },
  { value: "7.5", label: "7.5mg" },
  { value: "9", label: "9mg" },
  { value: "10", label: "10mg" },
  { value: "12.5", label: "12.5mg" },
  { value: "custom", labelKey: "otherCustom" },
];

// Peptide strength options in mg
const PEPTIDE_STRENGTH_OPTIONS = [
  { value: "1", label: "1mg" },
  { value: "5", label: "5mg" },
  { value: "10", label: "10mg" },
  { value: "15", label: "15mg" },
  { value: "20", label: "20mg" },
  { value: "30", label: "30mg" },
  { value: "50", label: "50mg" },
  { value: "custom", labelKey: "otherCustom" },
];

// Bacteriostatic water options in ml
const WATER_OPTIONS = [
  { value: "0.5", label: "0.5ml" },
  { value: "1.0", label: "1.0ml" },
  { value: "1.5", label: "1.5ml" },
  { value: "2.0", label: "2.0ml" },
  { value: "2.5", label: "2.5ml" },
  { value: "3.0", label: "3.0ml" },
  { value: "custom", labelKey: "otherCustom" },
];

// Syringe volume options
const SYRINGE_OPTIONS = [
  { value: "0.3", label: "0.3ml (30 units)", units: 30 },
  { value: "0.5", label: "0.5ml (50 units)", units: 50 },
  { value: "1.0", label: "1.0ml (100 units)", units: 100 },
];

export const PeptideCalculator = () => {
  const { t } = useTranslation();
  const [desiredDose, setDesiredDose] = useState("1");
  const [customDose, setCustomDose] = useState("");
  const [peptideStrength, setPeptideStrength] = useState("50");
  const [customStrength, setCustomStrength] = useState("");
  const [waterVolume, setWaterVolume] = useState("1.5");
  const [customWater, setCustomWater] = useState("");
  const [syringeVolume, setSyringeVolume] = useState("1.0");
  
  // Get actual values (considering custom inputs)
  const actualDose = useMemo(() => {
    if (desiredDose === "custom") {
      return parseFloat(customDose) || 0;
    }
    return parseFloat(desiredDose);
  }, [desiredDose, customDose]);

  const actualStrength = useMemo(() => {
    if (peptideStrength === "custom") {
      return parseFloat(customStrength) || 0;
    }
    return parseFloat(peptideStrength);
  }, [peptideStrength, customStrength]);

  const actualWater = useMemo(() => {
    if (waterVolume === "custom") {
      return parseFloat(customWater) || 0;
    }
    return parseFloat(waterVolume);
  }, [waterVolume, customWater]);

  const actualSyringe = useMemo(() => {
    return parseFloat(syringeVolume);
  }, [syringeVolume]);

  const syringeUnits = useMemo(() => {
    const option = SYRINGE_OPTIONS.find(o => o.value === syringeVolume);
    return option?.units || 100;
  }, [syringeVolume]);

  // Calculate the result
  const calculation = useMemo(() => {
    if (!actualDose || !actualStrength || !actualWater || !actualSyringe) {
      return null;
    }

    // Concentration: mg per ml of reconstituted solution
    const concentration = actualStrength / actualWater; // mg/ml
    
    // Volume needed: ml to draw for desired dose
    const volumeNeeded = actualDose / concentration; // ml
    
    // Percentage of syringe
    const percentage = (volumeNeeded / actualSyringe) * 100;
    
    // Units to draw (assuming 100 units = 1ml for standard syringes)
    const unitsPerMl = syringeUnits / actualSyringe;
    const unitsToDraw = volumeNeeded * unitsPerMl;

    return {
      volumeNeeded: volumeNeeded.toFixed(4),
      volumeNeededFormatted: volumeNeeded < 0.01 ? volumeNeeded.toFixed(4) : volumeNeeded.toFixed(3),
      percentage: percentage.toFixed(1),
      unitsToDraw: unitsToDraw.toFixed(1),
      isValid: volumeNeeded <= actualSyringe && volumeNeeded > 0
    };
  }, [actualDose, actualStrength, actualWater, actualSyringe, syringeUnits]);

  const getOptionLabel = (option) => {
    if (option.labelKey) {
      return t(`calculator.${option.labelKey}`);
    }
    return option.label;
  };

  return (
    <div className="w-full max-w-2xl mx-auto p-4 md:p-6">
      {/* Form Fields - clean minimal style like reference */}
      <div className="space-y-6">
        {/* Desired Dose */}
        <div className="space-y-2">
          <Label className="text-lg font-bold italic text-slate-800">
            {t('calculator.desiredDose')}
          </Label>
          <Select value={desiredDose} onValueChange={setDesiredDose}>
            <SelectTrigger className="h-14 text-lg bg-white border-2 border-slate-300 hover:border-[#4d82a9]/60 focus:border-[#4d82a9] transition-colors rounded-lg">
              <SelectValue placeholder={t('calculator.selectDose')} />
            </SelectTrigger>
            <SelectContent>
              {DOSE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {getOptionLabel(option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {desiredDose === "custom" && (
            <Input
              type="number"
              placeholder={t('calculator.enterDose')}
              value={customDose}
              onChange={(e) => setCustomDose(e.target.value)}
              className="h-14 text-lg border-2 border-slate-300 mt-2"
              step="0.01"
              min="0"
            />
          )}
        </div>

        {/* Peptide Strength */}
        <div className="space-y-2">
          <Label className="text-lg font-bold italic text-slate-800">
            {t('calculator.peptideStrength')}
          </Label>
          <Select value={peptideStrength} onValueChange={setPeptideStrength}>
            <SelectTrigger className="h-14 text-lg bg-white border-2 border-slate-300 hover:border-[#4d82a9]/60 focus:border-[#4d82a9] transition-colors rounded-lg">
              <SelectValue placeholder={t('calculator.selectStrength')} />
            </SelectTrigger>
            <SelectContent>
              {PEPTIDE_STRENGTH_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {getOptionLabel(option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {peptideStrength === "custom" && (
            <Input
              type="number"
              placeholder={t('calculator.enterStrength')}
              value={customStrength}
              onChange={(e) => setCustomStrength(e.target.value)}
              className="h-14 text-lg border-2 border-slate-300 mt-2"
              step="0.1"
              min="0"
            />
          )}
        </div>

        {/* Bacteriostatic Water */}
        <div className="space-y-2">
          <Label className="text-lg font-bold italic text-slate-800">
            {t('calculator.bacteriostaticWater')}
          </Label>
          <Select value={waterVolume} onValueChange={setWaterVolume}>
            <SelectTrigger className="h-14 text-lg bg-white border-2 border-slate-300 hover:border-[#4d82a9]/60 focus:border-[#4d82a9] transition-colors rounded-lg">
              <SelectValue placeholder={t('calculator.selectWater')} />
            </SelectTrigger>
            <SelectContent>
              {WATER_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {getOptionLabel(option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {waterVolume === "custom" && (
            <Input
              type="number"
              placeholder={t('calculator.enterVolume')}
              value={customWater}
              onChange={(e) => setCustomWater(e.target.value)}
              className="h-14 text-lg border-2 border-slate-300 mt-2"
              step="0.1"
              min="0"
            />
          )}
        </div>

        {/* Syringe Volume */}
        <div className="space-y-2">
          <Label className="text-lg font-bold italic text-slate-800">
            {t('calculator.syringeVolume')}
          </Label>
          <Select value={syringeVolume} onValueChange={setSyringeVolume}>
            <SelectTrigger className="h-14 text-lg bg-white border-2 border-slate-300 hover:border-[#4d82a9]/60 focus:border-[#4d82a9] transition-colors rounded-lg">
              <SelectValue placeholder={t('calculator.selectSyringe')} />
            </SelectTrigger>
            <SelectContent>
              {SYRINGE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Result Section - Blue theme consistent with site */}
      {calculation && (
        <div className="mt-8 animate-fade-in-up">
          <div className="bg-[#4d82a9] rounded-xl p-6 text-white">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">
              {t('calculator.result')}
            </h2>
            
            {calculation.isValid ? (
              <>
                <p className="text-center text-base md:text-lg">
                  {t('calculator.forDose', {
                    dose: actualDose,
                    volume: calculation.volumeNeededFormatted,
                    percentage: calculation.percentage,
                    syringe: actualSyringe
                  })}
                </p>
                <p className="text-center text-sm md:text-base mt-1 opacity-80">
                  ≈ {calculation.unitsToDraw} {t('calculator.units', { units: syringeUnits })}
                </p>
                
                {/* Syringe Visualization */}
                <SyringeVisualization 
                  fillPercentage={parseFloat(calculation.percentage)}
                  totalUnits={syringeUnits}
                  syringeVolume={actualSyringe}
                />
              </>
            ) : (
              <div className="text-center">
                <p className="text-lg">
                  {parseFloat(calculation.volumeNeeded) > actualSyringe 
                    ? `⚠️ ${t('calculator.volumeExceeds')}`
                    : t('calculator.checkValues')}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Info footer */}
      <p className="text-center text-xs text-muted-foreground mt-4 px-4">
        {t('calculator.educationalPurposes')}
      </p>
    </div>
  );
};

export default PeptideCalculator;
